import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Phone, BookOpen, Users, ArrowRight } from "lucide-react";
import CTASection from "../components/CTASection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      toast.success("Message sent! We'll get back to you within 2 hours.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const channels = [
    { icon: <MessageCircle size={22} />, t: "Live chat", d: "Average response: 90 seconds. Available 24/7.", a: "Start chat" },
    { icon: <Mail size={22} />, t: "Email", d: "support@brivo.app — within 2 hours.", a: "Send email" },
    { icon: <Phone size={22} />, t: "Phone", d: "+1 (415) 555-0199 — Mon-Fri, 9am-9pm UTC.", a: "Call us" },
    { icon: <BookOpen size={22} />, t: "Help docs", d: "Searchable guides, tutorials, and walkthroughs.", a: "Browse docs" },
  ];

  return (
    <div data-testid="support-page">
      <section className="hero-gradient relative overflow-hidden grain">
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#12b571]/10 text-black text-xs font-semibold mb-6">Support</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.05]">
            Real humans.<br />
            <span className="italic text-black/60 font-medium">Real answers, fast.</span>
          </h1>
          <p className="mt-7 text-lg text-black/70 max-w-2xl mx-auto leading-relaxed">
            Whatever you need — onboarding help, technical questions, or strategic advice — our team is one click away.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {channels.map((c) => (
              <div key={c.t} className="bg-[#FFFCF2] rounded-3xl p-7 border border-[#12b571]/8 card-hover">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-black mb-5">{c.icon}</div>
                <h3 className="font-display font-bold text-xl text-black mb-2">{c.t}</h3>
                <p className="text-sm text-black/70 mb-4 leading-relaxed">{c.d}</p>
                <button className="text-sm font-semibold text-black inline-flex items-center gap-1 hover:gap-2 transition-all">
                  {c.a} <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#FFE4E8]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">Send us a message</h2>
            <p className="mt-4 text-black/70">Fill out the form and we'll get back to you within 2 hours.</p>
          </div>
          <form onSubmit={onSubmit} className="bg-white rounded-3xl p-8 md:p-10 border border-[#12b571]/8 shadow-xl space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-black mb-2 block">Your name *</label>
                <input
                  data-testid="support-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
                  placeholder="Adunni Ola"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-black mb-2 block">Email *</label>
                <input
                  data-testid="support-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
                  placeholder="you@business.com"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-black mb-2 block">Company</label>
              <input
                data-testid="support-company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
                placeholder="Lush Boutique"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-black mb-2 block">How can we help? *</label>
              <textarea
                data-testid="support-message"
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2] resize-none"
                placeholder="Tell us what you're working on..."
              />
            </div>
            <button
              data-testid="support-submit"
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send message"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#E8F5E9]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Users size={40} className="mx-auto text-black mb-6" />
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">
            Or get help from the community.
          </h2>
          <p className="mt-5 text-lg text-black/70 max-w-xl mx-auto leading-relaxed">
            Join 12,000+ Brivo merchants in our community to share tips, swap stories, and grow together.
          </p>
        </div>
      </section>

      <CTASection accent="cream" />
    </div>
  );
}
